/**
 * NP20-1285: Vorlagen und Fassungen, die "Herunterladen" im Profil Export erzeugt.
 * Namen = Konstanten des Modell-Enums `ExportTemplate` bzw. der Generator-`Company`.
 * Die Verfuegbarkeitsregel steht synchron auch im BE (`ExportConfigurationValidator`) und im
 * Generator (`export_worker.py`): Katalogvorlagen nur mit gewaehltem Katalog in RVSE-Version 9.
 */
export type ExportTemplate =
  | 'SPIRIT_TESTING'
  | 'TEST_SOLUTIONS'
  | 'ATOS'
  | 'T_SYSTEMS'
  | 'ISO_ITZ_BUND'
  | 'ALLIANZ_ADESSO_MITTELSTAND'
  | 'ALTEN'
  | 'ERNST_YOUNG_RVSE9'
  | 'BA'
  | 'PLAIN_TEXT';

export type ExportVariant = 'LONG' | 'SHORT' | 'LONG_ANON' | 'SHORT_ANON';

export interface ExportTemplateSelection {
  template: ExportTemplate;
  variants: ExportVariant[];
}

export interface CatalogueVersion {
  id: string;
  rvseVersion: number;
}

const CATALOGUE_RVSE_VERSION = 9;

function fixedTemplates(): ExportTemplate[] {
  return [
    'SPIRIT_TESTING',
    'TEST_SOLUTIONS',
    'ATOS',
    'T_SYSTEMS',
    'ISO_ITZ_BUND',
  ];
}

function catalogueTemplates(): ExportTemplate[] {
  return ['ALLIANZ_ADESSO_MITTELSTAND', 'ALTEN', 'ERNST_YOUNG_RVSE9', 'BA'];
}

function templateOrder(): ExportTemplate[] {
  return [...fixedTemplates(), ...catalogueTemplates(), 'PLAIN_TEXT'];
}

function allVariants(): ExportVariant[] {
  return ['LONG', 'SHORT', 'LONG_ANON', 'SHORT_ANON'];
}

export function defaultExportTemplates(): ExportTemplateSelection[] {
  return [{ template: 'SPIRIT_TESTING', variants: ['LONG'] }];
}

/** Waehlbare Fassungen; die Textfassung hat keine Wahl und wird immer als LONG erzeugt. */
export function variantsOf(template: ExportTemplate): ExportVariant[] {
  return template === 'PLAIN_TEXT' ? [] : allVariants();
}

export function availableExportTemplates(
  catalogs: CatalogueVersion[],
  selectedCatalogIds: string[],
): ExportTemplate[] {
  const selected = new Set(selectedCatalogIds);
  const withCatalogue = catalogs.some(
    (c) => selected.has(c.id) && c.rvseVersion === CATALOGUE_RVSE_VERSION,
  );
  return withCatalogue ? templateOrder() : [...fixedTemplates(), 'PLAIN_TEXT'];
}

/**
 * Bestand ohne Auswahl, nur mit nicht mehr verfuegbaren Vorlagen oder ohne Fassungen faellt auf
 * SPIRIT Lang zurueck. Reihenfolge wie im Modal, damit der Vergleich reihenfolgeunabhaengig ist.
 */
export function normalizeExportTemplates(
  saved: ExportTemplateSelection[] | null | undefined,
  available: ExportTemplate[],
): ExportTemplateSelection[] {
  const allowed = new Set(available);
  const result: ExportTemplateSelection[] = [];
  for (const template of templateOrder()) {
    if (!allowed.has(template)) continue;
    const chosen = new Set(
      (saved ?? [])
        .filter((s) => s.template === template)
        .flatMap((s) => s.variants ?? []),
    );
    const variants =
      template === 'PLAIN_TEXT'
        ? chosen.size > 0
          ? (['LONG'] as ExportVariant[])
          : []
        : allVariants().filter((v) => chosen.has(v));
    if (variants.length > 0) result.push({ template, variants });
  }
  return result.length > 0 ? result : defaultExportTemplates();
}

export function sameExportTemplates(
  a: ExportTemplateSelection[] | null | undefined,
  b: ExportTemplateSelection[] | null | undefined,
): boolean {
  const all = templateOrder();
  return (
    JSON.stringify(normalizeExportTemplates(a, all)) ===
    JSON.stringify(normalizeExportTemplates(b, all))
  );
}

export function countExportFiles(selection: ExportTemplateSelection[]): number {
  return selection.reduce((sum, s) => sum + s.variants.length, 0);
}

/** Gruppen im Vorlagen-Modal, in Modal-Reihenfolge; leere Gruppen entfallen. */
export type ExportTemplateGroup = 'company' | 'catalogue' | 'further';

export function groupExportTemplates(
  available: ExportTemplate[],
): { group: ExportTemplateGroup; templates: ExportTemplate[] }[] {
  const allowed = new Set(available);
  const groups: { group: ExportTemplateGroup; templates: ExportTemplate[] }[] =
    [
      { group: 'company', templates: fixedTemplates() },
      { group: 'catalogue', templates: catalogueTemplates() },
      { group: 'further', templates: ['PLAIN_TEXT'] },
    ];
  return groups
    .map((g) => ({
      ...g,
      templates: g.templates.filter((t) => allowed.has(t)),
    }))
    .filter((g) => g.templates.length > 0);
}
